import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useUser } from "../store";
import { notify } from "../util/notify";
import { BreadCrumbs } from "./BreadCrumbs";
import { useConfirmation } from "./ConfirmationProvider";
import "./Backups.scss";

export default function Backups() {
  const { invokeApi, group, facility, payer } = useUser();
  const confirm = useConfirmation();
  const [backupsData, setBackupsData] = useState(null);

  const getBackups = useCallback(
    () =>
      invokeApi({
        endpoint: "/getBackups",
        data: { facility },
      }).then(setBackupsData),
    [facility, invokeApi]
  );

  useEffect(() => {
    getBackups();
  }, [getBackups]);

  const downloadFile = ({ sk }) => {
    invokeApi({
      endpoint: "/downloadUploadedReport",
      data: { backupSk: sk },
    }).then((data) => {
      const downloadLink = document.createElement("a");
      document.body.appendChild(downloadLink);
      downloadLink.href = `data:${data.contentType};base64,${data.content}`;
      downloadLink.target = "_self";
      downloadLink.download = data.filename;
      downloadLink.click();
      document.body.removeChild(downloadLink);
    });
  };

  const restoreBackup = async (backup) => {
    const confirmed = await confirm({
      title: "Restore this backup?",
      message: `Restore ${facility}-${payer} to ${backup.filename}? This will replace the current report data.`,
      confirmLabel: "Restore backup",
    });
    if (!confirmed) return;
    invokeApi({
      endpoint: "/restoreBackup",
      data: { backupSk: backup.sk },
    }).then(() => {
      notify.success("Backup has been restored successfully!");
      getBackups();
    });
  };

  const { active, backups } = backupsData || {};

  return (
    <>
      <BreadCrumbs
        path={[group, facility, payer, "backups"]}
        labels={["My Facilities", facility, payer, "backups"]}
      />
      <main className="backups-page">
        <header className="backups-heading">
          <div>
            <span className="backups-eyebrow">Report management</span>
            <h1>Backups</h1>
            <p>Download previous uploads or restore a report set.</p>
          </div>
          {backups && (
            <div className="backups-count" aria-label={`${backups.length} backups`}>
              <strong>{backups.length}</strong>
              <span>{backups.length === 1 ? "backup" : "backups"}</span>
            </div>
          )}
        </header>

        <section className="backups-card" aria-label="Report backups">
          {backupsData === null ? (
            <div className="backups-message" role="status">
              <span className="backups-message-icon" aria-hidden="true">
                <i className="fa-solid fa-circle-notch fa-spin" />
              </span>
              <p>Loading backups…</p>
            </div>
          ) : backups?.length ? (
            <div className="backups-table-wrap">
              <table className="backups-table">
                <thead>
                  <tr>
                    <th scope="col">Report file</th>
                    <th scope="col">Created</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => {
                    const isActive = active?.keys?.sk === backup.sk;

                    return (
                      <tr key={backup.sk} className={classNames({ "is-active-backup": isActive })}>
                        <td className="backups-file-cell">
                          <div className="backups-file-layout">
                            <span className="backups-file-icon" aria-hidden="true">
                              <i className="fa-solid fa-file-lines" />
                            </span>
                            <div className="backups-file-details">
                              <span className="backups-filename">{backup.filename}</span>
                              <span className="backups-file-caption">Uploaded report</span>
                            </div>
                          </div>
                        </td>
                        <td className="backups-date-cell">
                          <time dateTime={backup.sk}>
                            {new Date(backup.sk).toLocaleString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </time>
                        </td>
                        <td>
                          {isActive ? (
                            <span className="backups-status is-current">
                              <span aria-hidden="true" />
                              Current
                            </span>
                          ) : (
                            <span className="backups-status">Previous</span>
                          )}
                        </td>
                        <td>
                          <div className="backups-actions-cell">
                            <button
                              type="button"
                              className="backups-action-button"
                              onClick={() => downloadFile(backup)}
                              aria-label={`Download ${backup.filename}`}
                            >
                              <i className="fa-solid fa-download" aria-hidden="true" />
                              Download
                            </button>
                            {!isActive && (
                              <button
                                type="button"
                                className="backups-action-button is-restore"
                                onClick={() => restoreBackup(backup)}
                                aria-label={`Restore ${backup.filename}`}
                              >
                                <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="backups-message">
              <span className="backups-message-icon" aria-hidden="true">
                <i className="fa-solid fa-box-archive" />
              </span>
              <h2>No backups yet</h2>
              <p>Uploaded reports will appear here so you can download or restore them.</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
